/**
 * ScaleCalibrationPanel (facilitator)
 *
 * The S4 calibration step. For each scoring criterion in the session, the
 * facilitator confirms what each scale anchor means ("what does a 5 = High
 * represent?"), opens calibration for participants, and watches who reads the
 * scale the same way vs. differently — closing the "5 means different things to
 * different people" gap before scoring begins.
 *
 * Self-contained: derives its rows from the session's own criteria
 * (`criteriaToCalibrate` — `criteria_config` → `v3Config`, never the attributes
 * store's client defaults; #619), and persists via the scale calibration store.
 */

import { useEffect, useMemo, useState } from 'react';
import { Ruler, ChevronDown, ChevronRight, CheckCircle2, Users, AlertTriangle, Loader2 } from 'lucide-react';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useScaleCalibrationStore } from '../../../stores/prioritization/scaleCalibrationStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { MeasurementScale } from '../../../types/prioritization/lifecycle';
import type { CalibrationAnchor } from '../../../types/prioritization/scaleCalibration';
import { criteriaToCalibrate, type CalibrationCriterion } from '../../../lib/sessionCalibrationCriteria';

function anchorsFromScale(scale: MeasurementScale): CalibrationAnchor[] {
  return (scale.points ?? []).map(p => ({
    value: p.value,
    label: p.label,
    definition: p.description ?? '',
  }));
}

interface CriterionCalibrationRowProps {
  sessionId: string;
  criterion: CalibrationCriterion;
}

function CriterionCalibrationRow({ sessionId, criterion }: CriterionCalibrationRowProps) {
  const attr = criterion;
  const scale: MeasurementScale = criterion.scale;
  const userId = useUserStore(s => s.userId) ?? '';
  const calibrations = useScaleCalibrationStore(s => s.calibrations);
  const acks = useScaleCalibrationStore(s => s.acks);
  const upsertCalibration = useScaleCalibrationStore(s => s.upsertCalibration);
  const setCalibrationStatus = useScaleCalibrationStore(s => s.setCalibrationStatus);

  const calibration = useMemo(
    () =>
      calibrations.find(
        c => c.sessionId === sessionId && c.attributeKey === attr.key && (c.scaleId ?? '') === (scale.id ?? '')
      ),
    [calibrations, sessionId, attr.key, scale.id]
  );

  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [anchors, setAnchors] = useState<CalibrationAnchor[]>(
    calibration?.anchors?.length ? calibration.anchors : anchorsFromScale(scale)
  );

  useEffect(() => {
    if (calibration?.anchors?.length) setAnchors(calibration.anchors);
  }, [calibration?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const myAcks = calibration ? acks.filter(a => a.calibrationId === calibration.id) : [];
  const aligned = myAcks.filter(a => a.alignment === 'aligned').length;
  const differs = myAcks.filter(a => a.alignment === 'differs');

  const status = calibration?.status ?? 'draft';

  const persist = async (nextStatus?: 'draft' | 'open' | 'calibrated') => {
    setBusy(true);
    await upsertCalibration({
      sessionId,
      attributeKey: attr.key,
      attributeLabel: attr.label,
      scaleId: scale.id,
      scaleLabel: scale.label,
      anchors,
      status: nextStatus,
      actorId: userId,
    });
    setBusy(false);
  };

  const changeStatus = async (next: 'open' | 'calibrated') => {
    setBusy(true);
    if (!calibration) {
      await upsertCalibration({
        sessionId, attributeKey: attr.key, attributeLabel: attr.label,
        scaleId: scale.id, scaleLabel: scale.label, anchors, status: next, actorId: userId,
      });
    } else {
      await setCalibrationStatus({ calibrationId: calibration.id, status: next, actorId: userId });
    }
    setBusy(false);
  };

  return (
    <li className="px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => setExpanded(v => !v)} className="flex items-center gap-2 min-w-0 text-left">
          {expanded ? <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" /> : <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />}
          <span className="text-sm font-medium text-[var(--text-pri)] truncate">{attr.label}</span>
          <span className="text-xs text-[var(--text-ter)]">· {scale.label}</span>
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          {status === 'calibrated' ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
              <CheckCircle2 className="h-3.5 w-3.5" /> Calibrated
            </span>
          ) : status === 'open' ? (
            <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)]">
              <Users className="h-3.5 w-3.5" /> {aligned + differs.length} responded
            </span>
          ) : (
            <span className="text-xs text-[var(--text-ter)]">Not started</span>
          )}
          {differs.length > 0 && status !== 'calibrated' && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--mustard)]">
              <AlertTriangle className="h-3.5 w-3.5" /> {differs.length} differ
            </span>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 ml-6 space-y-3">
          {attr.definition && (
            <p className="text-xs text-[var(--text-ter)] italic">{attr.definition}</p>
          )}

          <div className="space-y-2">
            {anchors.map((anchor, i) => (
              <div key={`${anchor.value}-${i}`} className="flex items-start gap-2">
                <span className="mt-1.5 inline-flex items-center justify-center min-w-[2.25rem] px-2 py-0.5 rounded bg-[var(--hover-tint)] text-xs font-semibold text-[var(--text-sec)] flex-shrink-0">
                  {anchor.label}
                </span>
                <textarea
                  value={anchor.definition ?? ''}
                  onChange={e => {
                    const next = [...anchors];
                    next[i] = { ...anchor, definition: e.target.value };
                    setAnchors(next);
                  }}
                  rows={1}
                  placeholder={`What does "${anchor.label}" mean for ${attr.label}?`}
                  disabled={status === 'calibrated'}
                  className="flex-1 text-sm border border-[var(--rule)] px-2.5 py-1.5 focus:ring-2 focus:ring-indigo-300 focus:border-[var(--slate)] resize-none disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)]"
                />
              </div>
            ))}
          </div>

          {differs.length > 0 && (
            <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-2.5 space-y-1.5">
              <p className="text-xs font-semibold text-[var(--mustard)]">Read the scale differently:</p>
              {differs.map(a => (
                <p key={a.id} className="text-xs text-[var(--text-sec)]">
                  <span className="font-medium">{a.participantName || 'A participant'}</span>
                  {a.note ? <span className="text-[var(--text-ter)]"> — "{a.note}"</span> : null}
                </p>
              ))}
            </div>
          )}

          {status !== 'calibrated' && (
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => void persist()}
                disabled={busy}
                className="text-xs font-medium px-3 py-1.5 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save definitions'}
              </button>
              {status !== 'open' ? (
                <button
                  onClick={() => void changeStatus('open')}
                  disabled={busy}
                  className="text-xs font-semibold px-3 py-1.5 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
                >
                  Open for calibration
                </button>
              ) : (
                <button
                  onClick={() => void changeStatus('calibrated')}
                  disabled={busy}
                  className="text-xs font-semibold px-3 py-1.5 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50"
                >
                  Mark calibrated
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

interface ScaleCalibrationPanelProps {
  sessionId: string;
}

export function ScaleCalibrationPanel({ sessionId }: ScaleCalibrationPanelProps) {
  const attributes = useAttributesStore(s => s.attributes);
  const criteriaConfig = useSessionStore(s => s.sessions.find(x => x.id === sessionId)?.criteriaConfig ?? null);
  const modeConfig = useSessionStore(s => s.sessions.find(x => x.id === sessionId)?.modeConfig ?? null);
  const fetchForSession = useScaleCalibrationStore(s => s.fetchForSession);

  useEffect(() => {
    void fetchForSession(sessionId);
  }, [sessionId, fetchForSession]);

  // The session's own criteria, not the attributes store: the store merges
  // client defaults (Impact / Effort / Urgency with scales) into every
  // workspace, and calibrating those wrote governance rows keyed to criteria
  // the session never scored (#619).
  const scorable = useMemo(
    () => criteriaToCalibrate({ criteriaConfig, modeConfig }, attributes),
    [criteriaConfig, modeConfig, attributes]
  );

  if (scorable.length === 0) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] px-5 py-4 text-sm text-[var(--text-ter)]">
        This session has no scoring criteria to calibrate yet. Lock its criteria first.
      </div>
    );
  }

  return (
    <div className="border border-[var(--slate)] bg-[var(--bg-raised)] overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 bg-[var(--slate-dim)] border-b border-[var(--slate)]">
        <div className="w-8 h-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center flex-shrink-0">
          <Ruler className="h-4 w-4 text-[var(--text-ter)]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--text-pri)]">Calibrate scales</p>
          <p className="text-xs text-[var(--text-sec)]">
            Align what each anchor means before scoring. Open a criterion to participants and watch for divergent readings.
          </p>
        </div>
      </div>
      <ul className="divide-y divide-[var(--rule)]">
        {scorable.map(criterion => (
          <CriterionCalibrationRow key={criterion.key} sessionId={sessionId} criterion={criterion} />
        ))}
      </ul>
    </div>
  );
}

export default ScaleCalibrationPanel;
